import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import {
  Crown, ShieldCheck, CreditCard, Loader2, Check, Download, AlertTriangle,
  CalendarClock, RefreshCw, XCircle, ArrowUpRight, ArrowDownRight, Sparkles,
  Gift, Receipt, Plane, Gavel, ChevronRight,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const money2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const TIER_BADGE = {
  founder: "bg-amber-50 text-amber-700 border-amber-200",
  professional: "bg-cobalt-light text-cobalt border-cobalt/20",
  enterprise: "bg-indigo-50 text-indigo-700 border-indigo-200",
  vip: "bg-rose-50 text-rose-700 border-rose-200",
  custom: "bg-violet-50 text-violet-700 border-violet-200",
};
const STATUS_PILL = {
  Active: "bg-emerald-50 text-emerald-600",
  Trial: "bg-cyan-50 text-cyan-600",
  "Past Due": "bg-amber-50 text-amber-600 animate-pulse",
  Suspended: "bg-rose-50 text-rose-600",
  Cancelled: "bg-slate-100 text-slate-500",
  Inactive: "bg-slate-100 text-slate-500",
};

const fadeUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!target) return null;
  const diff = Math.max(0, new Date(target).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const Cell = ({ v, label }) => (
    <div className="text-center">
      <div className="font-mono font-bold text-xl sm:text-2xl text-white tabular-nums">{String(v).padStart(2, "0")}</div>
      <div className="text-[10px] uppercase tracking-widest text-cyan-300/70">{label}</div>
    </div>
  );
  return (
    <div className="flex items-center gap-3 sm:gap-4" data-testid="next-billing-countdown">
      <Cell v={d} label="Days" /><span className="text-cyan-300/40 text-xl">:</span>
      <Cell v={h} label="Hrs" /><span className="text-cyan-300/40 text-xl">:</span>
      <Cell v={m} label="Min" /><span className="text-cyan-300/40 text-xl">:</span>
      <Cell v={s} label="Sec" />
    </div>
  );
}

export default function BillingCenter() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [promo, setPromo] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/attorney/billing/overview"); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load billing"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, key) => {
    setBusy(key);
    try { await fn(); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed"); }
    finally { setBusy(""); }
  };

  const updatePayment = async () => {
    setBusy("pay");
    try { const { data } = await api.post("/attorney/membership/update-payment"); window.location.href = data.url; }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start checkout"); setBusy(""); }
  };
  const cancel = () => act(async () => { await api.post("/attorney/membership/cancel"); toast.success("Cancellation scheduled for period end"); }, "cancel");
  const reactivate = () => act(async () => { await api.post("/attorney/membership/reactivate"); toast.success("Membership reactivated"); }, "reactivate");
  const toggleRenew = (enabled) => act(async () => { await api.post("/attorney/membership/auto-renew", { enabled }); }, "renew");
  const changePlan = (plan) => act(async () => {
    const { data } = await api.post("/attorney/membership/change-plan", { plan_id: plan.plan_id });
    toast.success(`${data.direction === "downgrade" ? "Downgraded" : "Upgraded"} to ${plan.name}`);
  }, `plan-${plan.plan_id}`);
  const applyPromo = () => act(async () => {
    const { data } = await api.post("/attorney/membership/apply-promo", { code: promo.trim() });
    toast.success(`${data.code} applied — ${data.description}`); setPromo("");
  }, "promo");
  const downloadReceipt = async (txnId) => {    try {
      const res = await api.get(`/billing/receipt/${txnId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `receipt_${txnId}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Receipt unavailable"); }
  };

  if (!data) return <AppLayout><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div></AppLayout>;

  const m = data.membership;
  const plans = data.plans || [];
  const currentTier = (m.tier || "").toLowerCase();
  const pastDue = m.payment_failed || m.in_grace || m.display_status === "Past Due";

  return (
    <AppLayout>
      <motion.div variants={stagger} initial="hidden" animate="show"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="billing-center">

        <motion.div variants={fadeUp} className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-navy tracking-tight">Billing & Membership</h1>
            <p className="text-slate-500 mt-1">Manage your plan, payment method, and purchase history.</p>
          </div>
        </motion.div>

        {pastDue && (
          <motion.div variants={fadeUp} data-testid="billing-warning-banner"
            className="rounded-2xl bg-rose-50 border border-rose-200 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-rose-100 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5 text-rose-600" /></div>
              <div>
                <div className="font-semibold text-rose-700">Payment failed — action required</div>
                <div className="text-sm text-rose-600/90">
                  Update your payment method to keep your access.
                  {m.days_until_suspension != null && (
                    <> Access will be suspended in <span className="font-bold" data-testid="suspension-countdown">{m.days_until_suspension} day{m.days_until_suspension === 1 ? "" : "s"}</span>.</>
                  )}
                </div>
              </div>
            </div>
            <button onClick={updatePayment} disabled={busy === "pay"} data-testid="banner-update-payment"
              className="shrink-0 inline-flex items-center gap-2 bg-rose-600 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-rose-700 transition-all active:scale-95">
              {busy === "pay" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Update Payment Method
            </button>
          </motion.div>
        )}

        {/* Hero current membership */}
        <motion.div variants={fadeUp} className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgb(10,25,47,0.12)]">
          <div className="relative bg-navy text-white p-7 sm:p-9">
            <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 85% 0%, rgba(56,189,248,0.35), transparent 55%)" }} />
            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border ${TIER_BADGE[currentTier] || "bg-white/10 text-cyan-200 border-white/20"}`} data-testid="plan-tier-badge">
                    <Crown className="w-3.5 h-3.5" /> {m.badge || m.membership_level || "Membership"}
                  </span>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_PILL[m.display_status] || STATUS_PILL.Inactive}`} data-testid="membership-status-pill">
                    {m.display_status}
                  </span>
                  {m.canceled && <span className="text-xs text-amber-300/90">Cancels at period end</span>}
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mt-4" data-testid="current-plan-name">{m.membership_level || "Attorney Membership"}</h2>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-extrabold text-cyan-300">{money(m.monthly_price)}</span>
                  <span className="text-slate-300 text-sm">/month</span>
                </div>
                {(m.grandfathered || m.promo_label) && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-amber-300/90 bg-amber-400/10 border border-amber-400/20 rounded-full px-3 py-1" data-testid="promo-callout">
                    <Gift className="w-3.5 h-3.5" /> {m.promo_label || "Grandfathered pricing locked in"}
                  </div>
                )}
              </div>

              <div className="lg:text-right">
                <div className="flex items-center gap-2 text-cyan-300/70 text-xs uppercase tracking-widest lg:justify-end mb-2">
                  <CalendarClock className="w-3.5 h-3.5" /> {m.canceled ? "Access until" : "Next renewal"} · {fmtDate(m.next_billing_date)}
                </div>
                <Countdown target={m.next_billing_date} />
              </div>
            </div>
          </div>

          {/* control strip */}
          <div className="bg-white p-5 sm:p-6 flex flex-wrap items-center gap-3 justify-between border-x border-b border-slate-100 rounded-b-3xl">
            <div className="flex items-center gap-3" data-testid="auto-renew-row">
              <button type="button" role="switch" aria-checked={m.auto_renew} disabled={m.canceled || busy === "renew"}
                onClick={() => toggleRenew(!m.auto_renew)} data-testid="auto-renew-toggle"
                className={`w-12 h-6 rounded-full transition-colors relative shrink-0 ${m.auto_renew ? "bg-cobalt" : "bg-slate-300"} disabled:opacity-50`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${m.auto_renew ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
              <span className="text-sm text-slate-600 whitespace-nowrap">Auto-renew {m.auto_renew ? "on" : "off"}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={updatePayment} disabled={busy === "pay"} data-testid="update-payment-button"
                className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:-translate-y-0.5 transition-all active:scale-95 disabled:opacity-60">
                {busy === "pay" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Update Payment Method
              </button>
              {m.canceled ? (
                <button onClick={reactivate} disabled={busy === "reactivate"} data-testid="reactivate-membership-button"
                  className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-emerald-100 transition-all active:scale-95">
                  {busy === "reactivate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reactivate
                </button>
              ) : (
                <button onClick={cancel} disabled={busy === "cancel"} data-testid="cancel-membership-button"
                  className="inline-flex items-center gap-2 bg-rose-50 text-rose-600 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-rose-500 hover:text-white transition-all active:scale-95">
                  {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Cancel Membership
                </button>
              )}
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Benefits */}
          <motion.div variants={fadeUp} className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 p-6 sm:p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h3 className="font-heading font-bold text-navy flex items-center gap-2"><Sparkles className="w-5 h-5 text-cobalt" /> Included with your plan</h3>
            <div className="mt-4 grid sm:grid-cols-2 gap-2.5" data-testid="benefits-list">
              {(m.benefits && m.benefits.length ? m.benefits : ["Full Attorney Portal & Firm Command Center", "Marketplace case access", "Response Network & RN Vetting", "Cancel anytime"]).map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="mt-0.5 h-4 w-4 rounded-full bg-emerald-100 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-emerald-600" /></span> {b}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Payment method */}
          <motion.div variants={fadeUp} className="bg-white rounded-3xl border border-slate-100 p-6 sm:p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="payment-method-card">
            <h3 className="font-heading font-bold text-navy flex items-center gap-2"><CreditCard className="w-5 h-5 text-cobalt" /> Payment method</h3>
            <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-100 p-4">
              {m.payment_method ? (
                <div className="flex items-center gap-3">
                  <div className="h-9 w-12 rounded-lg bg-navy flex items-center justify-center text-white text-[10px] font-bold uppercase">{m.payment_method.brand}</div>
                  <div><div className="font-semibold text-navy">•••• {m.payment_method.last4}</div><div className="text-xs text-slate-400">On file</div></div>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-sm text-slate-500">
                  <ShieldCheck className="w-5 h-5 text-cobalt" /> Secured by Stripe Checkout
                </div>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-3">Payments are processed on Stripe's secure checkout. Use “Update Payment Method” to re-authorize.</p>

            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="promo-apply">
              <div className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-2"><Gift className="w-3.5 h-3.5 text-cobalt" /> Have a promo code?</div>
              <div className="flex items-center gap-2">
                <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} data-testid="promo-input" placeholder="Enter code"
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono outline-none focus:border-cobalt" />
                <button onClick={applyPromo} disabled={busy === "promo" || !promo.trim()} data-testid="promo-apply-btn"
                  className="inline-flex items-center gap-1.5 bg-navy text-white px-4 py-2 rounded-xl text-sm font-semibold hover:-translate-y-0.5 transition-all active:scale-95 disabled:opacity-50">
                  {busy === "promo" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Plan selector */}
        {plans.length > 1 && (
          <motion.div variants={fadeUp}>
            <h3 className="font-heading font-bold text-navy text-xl mb-3">Change your plan</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plan-selector">
              {plans.map((p) => {
                const isCurrent = (p.plan_id === m.plan_id) || (p.name === m.membership_level);
                const up = p.monthly_price > m.monthly_price;
                const tier = (p.tier || "").toLowerCase();
                return (
                  <div key={p.plan_id} data-testid={`plan-card-${p.plan_id}`}
                    className={`rounded-3xl border p-6 bg-white transition-all ${isCurrent ? "ring-2 ring-cobalt border-cobalt shadow-[0_12px_40px_rgb(30,64,175,0.12)]" : "border-slate-100 hover:border-cobalt/40 hover:-translate-y-1"}`}>
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${TIER_BADGE[tier] || "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        <Crown className="w-3 h-3" /> {p.badge || p.name}
                      </span>
                      {isCurrent && <span className="text-xs font-semibold text-cobalt">Current</span>}
                    </div>
                    <div className="mt-3 font-heading text-lg font-bold text-navy">{p.name}</div>
                    <div className="flex items-baseline gap-1"><span className="font-heading text-3xl font-extrabold text-navy">{money(p.monthly_price)}</span><span className="text-slate-400 text-sm">/mo</span></div>
                    {p.promo_label && <div className="mt-1 text-xs text-amber-600">{p.promo_label}</div>}
                    <div className="mt-4 space-y-1.5 min-h-[96px]">
                      {(p.benefits || []).slice(0, 4).map((b, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs text-slate-500"><Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> {b}</div>
                      ))}
                    </div>
                    {isCurrent ? (
                      <button disabled className="mt-4 w-full py-2.5 rounded-full bg-slate-100 text-slate-400 text-sm font-semibold cursor-default">Your current plan</button>
                    ) : (
                      <button onClick={() => changePlan(p)} disabled={busy === `plan-${p.plan_id}`} data-testid={`change-plan-${p.plan_id}`}
                        className={`mt-4 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full text-sm font-semibold transition-all active:scale-95 ${up ? "bg-navy text-white hover:-translate-y-0.5" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        {busy === `plan-${p.plan_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : up ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                        {up ? "Upgrade" : "Downgrade"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Purchase histories */}
        <motion.div variants={fadeUp} className="grid gap-6">
          <HistoryTable title="Billing history" icon={Receipt} rows={data.history.membership} onDownload={downloadReceipt} testid="history-membership" />
          <HistoryTable title="Marketplace claim purchases" icon={Gavel} rows={data.history.marketplace_claim} onDownload={downloadReceipt} testid="history-claims" />
          <HistoryTable title="Drone deployment purchases" icon={Plane} rows={data.history.drone_deployment} onDownload={downloadReceipt} testid="history-drones" />
        </motion.div>
      </motion.div>
    </AppLayout>
  );
}

function HistoryTable({ title, icon: Icon, rows, onDownload, testid }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 sm:p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={testid}>
      <h3 className="font-heading font-bold text-navy flex items-center gap-2 mb-4"><Icon className="w-5 h-5 text-cobalt" /> {title}</h3>
      {rows?.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b border-slate-100">
              <th className="py-2 font-semibold">Description</th><th className="py-2 font-semibold">Date</th><th className="py-2 font-semibold">Amount</th><th className="py-2 font-semibold">Status</th><th className="py-2 font-semibold text-right">Receipt</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.txn_id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                  <td className="py-3 text-navy font-medium">{r.label || r.kind}</td>
                  <td className="py-3 text-slate-500">{fmtDate(r.date)}</td>
                  <td className="py-3 font-semibold text-navy">{money2(r.amount)}</td>
                  <td className="py-3"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">Paid</span></td>
                  <td className="py-3 text-right">
                    <button onClick={() => onDownload(r.txn_id)} data-testid={`receipt-download-${r.txn_id}`}
                      className="inline-flex items-center gap-1 text-cobalt hover:text-cobalt-hover p-2 rounded-full hover:bg-cobalt-light transition-colors"><Download className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-slate-400">No transactions yet.</p>}
    </div>
  );
}
